import React from 'react';
import { FormData } from '../CareersApplicationForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface ReviewStepProps {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({ formData }) => {
  const formatPositionType = (positionType: string) => {
    return positionType.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatExperienceLevel = (level: string) => {
    const levels = {
      'beginner': 'Beginner',
      'intermediate': 'Intermediate', 
      'advanced': 'Advanced',
      'expert': 'Expert'
    };
    return levels[level as keyof typeof levels] || level;
  };

  const formatWorkPreference = (preference: string) => {
    const preferences = {
      'remote': 'Remote',
      'hybrid': 'Hybrid',
      'onsite': 'On-site',
      'flexible': 'Flexible/Open to discuss'
    };
    return preferences[preference as keyof typeof preferences] || preference;
  };

  const isTechnicalRole = formData.positionType.includes('developer') ||
                         formData.positionType.includes('engineer') ||
                         formData.positionType === 'data-engineer' ||
                         formData.positionType === 'devops-engineer' ||
                         formData.positionType === 'ai-engineer';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Review Your Application</h3>
        <p className="text-sm text-gray-600 mb-6">Please review all information before submitting your application.</p>
      </div>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div><strong>Name:</strong> {formData.firstName} {formData.lastName}</div>
          <div><strong>Email:</strong> {formData.email}</div>
          {formData.phone && <div><strong>Phone:</strong> {formData.phone}</div>}
          <div><strong>Location:</strong> {formData.location}</div>
          <div><strong>Position:</strong> {formatPositionType(formData.positionType)}</div>
        </CardContent>
      </Card>

      {/* AI Experience */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">AI Experience</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div><strong>Experience Level:</strong> {formatExperienceLevel(formData.aiExperienceLevel)}</div>
          <div><strong>Favorite AI Tool:</strong> {formData.favoriteAITool}</div>
          <div>
            <strong>AI Tools Used:</strong>
            <div className="flex flex-wrap gap-1 mt-1">
              {formData.aiToolsUsed.map((tool, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tool}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <strong>AI Project Description:</strong>
            <p className="text-sm text-gray-600 mt-1">{formData.aiProjectDescription}</p>
          </div>
        </CardContent>
      </Card>

      {/* Technical Information */}
      {isTechnicalRole && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Technical Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {formData.githubUrl && <div><strong>GitHub:</strong> <a href={formData.githubUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{formData.githubUrl}</a></div>}
            {formData.linkedinUrl && <div><strong>LinkedIn:</strong> <a href={formData.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{formData.linkedinUrl}</a></div>}
            {formData.portfolioUrl && <div><strong>Portfolio:</strong> <a href={formData.portfolioUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{formData.portfolioUrl}</a></div>}
            
            {formData.technicalSkills && formData.technicalSkills.length > 0 && (
              <div>
                <strong>Technical Skills:</strong>
                <div className="flex flex-wrap gap-1 mt-1">
                  {formData.technicalSkills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {formData.preferredLanguages && formData.preferredLanguages.length > 0 && (
              <div>
                <strong>Preferred Languages:</strong>
                <div className="flex flex-wrap gap-1 mt-1">
                  {formData.preferredLanguages.map((lang, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {lang}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Professional Experience */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Professional Experience</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div><strong>Current Role:</strong> {formData.currentRole}</div>
          <div><strong>Experience:</strong> {formData.experience} years</div>
          <div><strong>Availability Date:</strong> {new Date(formData.availabilityDate).toLocaleDateString()}</div>
          <div><strong>Work Preference:</strong> {formatWorkPreference(formData.workPreference)}</div>
          {formData.salaryExpectation && <div><strong>Salary Expectation:</strong> {formData.salaryExpectation}</div>}
          
          <div>
            <strong>Education:</strong>
            <p className="text-sm text-gray-600 mt-1">{formData.education}</p>
          </div>
          
          {formData.certifications && (
            <div>
              <strong>Certifications:</strong>
              <p className="text-sm text-gray-600 mt-1">{formData.certifications}</p>
            </div>
          )}
          
          <div>
            <strong>Why join us:</strong>
            <p className="text-sm text-gray-600 mt-1">{formData.whyJoinUs}</p>
          </div>
          
          {formData.coverLetter && (
            <div>
              <strong>Additional Information:</strong>
              <p className="text-sm text-gray-600 mt-1">{formData.coverLetter}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReviewStep;